/**
 * Centralized "is this an image?" heuristics for messaging.
 *
 * Notes:
 * - Upload thumbnails operate on browser `File.type` (MIME). Some browsers may provide empty/unknown MIME types.
 * - Message attachments may provide `document.fileType` as a MIME type or an extension-like string.
 * - We intentionally treat `heic`/`tiff` as images, but we do not use them as *thumbnails* due to poor browser support.
 */
const IMAGE_EXTENSIONS = new Set([
  'png',
  'jpg',
  'jpeg',
  'gif',
  'bmp',
  'tif',
  'tiff',
  'heic',
  'webp',
]);

export function isImageType(fileType?: string): boolean {
  if (!fileType) return false;

  const normalized = fileType.trim().toLowerCase();
  if (!normalized) return false;

  // MIME type (preferred for browser Files)
  if (normalized.startsWith('image/')) return true;

  // Extension-like strings
  const ext = normalized.startsWith('.') ? normalized.slice(1) : normalized;
  return IMAGE_EXTENSIONS.has(ext);
}

export function isImageThumbnail(file: File): boolean {
  const mimeType = file?.type?.toLowerCase() ?? '';

  // Thumbnailing depends on MIME type support, so require a MIME type.
  if (!mimeType.startsWith('image/')) return false;

  // Exclude types that commonly fail to preview reliably in browsers.
  // Some browsers report TIFF as `image/tif` (non-standard), so exclude both `image/tif` and `image/tiff`.
  if (mimeType.startsWith('image/tif') || mimeType.includes('heic'))
    return false;

  return true;
}
